import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CheckHomeBuyerPasscodeRequest,
  CheckHomeBuyerPasscodeResponse,
  CheckHomeBuyerPasscodeResponseSchema,
  SetupHomeBuyerPasscodeRequest,
  SetupHomeBuyerPasscodeResponse,
  SetupHomeBuyerPasscodeResponseSchema,
  ValidateHomeBuyerPasscodeRequest,
  ValidateHomeBuyerPasscodeResponse,
  ValidateHomeBuyerPasscodeResponseSchema,
} from '../../models/passcode.model';
import { SmartrService } from '../smartr.service';

@Injectable({
  providedIn: 'root',
})
export class PasscodeService extends SmartrService {
  private readonly http = inject(HttpClient);

  /**
   * Checks if a home buyer passcode exists for the given brokerage and client
   * @param request - The check request containing brokerageId and clientId
   * @returns Observable that emits true if passcode exists, false if not found
   */
  check(request: CheckHomeBuyerPasscodeRequest): Observable<boolean> {
    return this.http
      .post<CheckHomeBuyerPasscodeResponse>(environment.homeBuyerPasscode.check, request)
      .pipe(
        this.verifyResponse(CheckHomeBuyerPasscodeResponseSchema),
        map((response) => response.exists),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(false);
          }
          throw error;
        })
      );
  }

  /**
   * Sets up a passcode for a home buyer.
   * @param request The request data for setting up the passcode.
   * @returns An observable that emits true if successful, false otherwise.
   */
  setup(request: SetupHomeBuyerPasscodeRequest): Observable<boolean> {
    return this.http
      .post<SetupHomeBuyerPasscodeResponse>(environment.homeBuyerPasscode.setup, request)
      .pipe(
        this.verifyResponse(SetupHomeBuyerPasscodeResponseSchema),
        map((response) => response.status.toLowerCase() === 'success'),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 400 && error.error?.includes?.('already exists')) {
            return of(true);
          }
          throw error;
        })
      );
  }

  /**
   * Validates a home buyer passcode
   * @param request - The validation request containing brokerageId, clientId, and passcode
   * @returns Observable that emits true if the passcode is valid
   */
  validate(request: ValidateHomeBuyerPasscodeRequest): Observable<boolean> {
    return this.http
      .post<ValidateHomeBuyerPasscodeResponse>(environment.homeBuyerPasscode.validate, request)
      .pipe(
        this.verifyResponse(ValidateHomeBuyerPasscodeResponseSchema),
        map((response) => response.status.toLocaleLowerCase() === 'valid'),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 400) {
            return of(false);
          }

          throw error;
        })
      );
  }
}
